import { useAtom, useAtomValue } from 'jotai';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { countAtom, messageAtom } from '@/hooks/atoms';
import { useUsers } from '@/hooks/useUsers';
import { Atom, Palette, Server, Rocket } from 'lucide-react';
import { LabOSTopbar } from './lib/labos';

function App() {
  const [count, setCount] = useAtom(countAtom);
  const message = useAtomValue(messageAtom);
  const { users, loading, error, fetchUsers } = useUsers();

  return (
    <div className="flex flex-col h-screen">
      <LabOSTopbar appName="Content Lab Template" accentColor="#8b5cf6" />
      <main className="flex-1 overflow-auto">
        <div className="h-full bg-gradient-to-br from-slate-50 to-slate-100 p-8">
          <div className="max-w-4xl mx-auto">
            <header className="text-center mb-12">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">Content Lab Template</h1>
              <p className="text-lg text-slate-600">React + TypeScript + Vite + Shadcn/ui + Jotai + Firebase</p>
            </header>

            <div className="grid gap-8 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Atom className="h-5 w-5" />
                    Jotai State Management
                  </CardTitle>
                  <CardDescription>Testing atomic state management with Jotai</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">{message}</p>
                  <div className="flex items-center space-x-4">
                    <Button onClick={() => setCount(count + 1)} variant="default">
                      Count: {count}
                    </Button>
                    <Button onClick={() => setCount(0)} variant="outline">
                      Reset
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Shadcn/ui Components
                  </CardTitle>
                  <CardDescription>Beautiful and accessible component library</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">This template includes pre-configured Shadcn/ui components</p>
                  <div className="flex space-x-2">
                    <Button variant="default">Default</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="outline">Outline</Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5" />
                    Firebase Backend
                  </CardTitle>
                  <CardDescription>Serverless functions with Firebase</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-slate-600">Backend functions are ready in the /backend folder</p>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">Error: {error}</p>
                    </div>
                  )}

                  {users.length > 0 && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-700 font-medium mb-2">Users fetched:</p>
                      <ul className="text-xs text-green-600 space-y-1">
                        {users.map((user) => (
                          <li key={user.id}>
                            • {user.name} ({user.email})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Button onClick={fetchUsers} variant="outline" disabled={loading}>
                    {loading ? 'Loading...' : 'Fetch Users'}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Rocket className="h-5 w-5" />
                    Getting Started
                  </CardTitle>
                  <CardDescription>Quick setup instructions</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm space-y-2 text-slate-600">
                    <li>
                      • Run <code className="bg-slate-100 px-2 py-1 rounded">npm run dev</code> to start development
                    </li>
                    <li>
                      • Edit components in <code className="bg-slate-100 px-2 py-1 rounded">src/components</code>
                    </li>
                    <li>
                      • Add new pages in <code className="bg-slate-100 px-2 py-1 rounded">src/pages</code>
                    </li>
                    <li>
                      • Configure Firebase in <code className="bg-slate-100 px-2 py-1 rounded">backend/</code>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
